import React, { useState, useEffect, useMemo } from 'react';
import { Container, Box, Typography, Grid, CircularProgress } from '@mui/material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import Header from '../components/Header';
import DealCard from '../components/DealCard';
import FilterBar from '../components/FilterBar';
import Footer from '../components/Footer';
import MapView from '../components/MapView';
import SEOHead, { generateHomepageStructuredData } from '../components/SEOHead';
import { logger } from '../utils/logger';
import { useCityContext } from '../contexts/CityContext';
import SearchOffIcon from '@mui/icons-material/SearchOff';

const trimAddress = (address) => {
  if (!address) return '';
  return address.split(',')[0].trim();
};

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in km
};

const isTimeWithinSlot = (selectedHour, timeSlot) => {
  if (!timeSlot || !timeSlot.start || !timeSlot.end) return false;

  // Convert selected hour to number format (e.g., "14" -> 1400)
  const selectedTime = parseInt(selectedHour) * 100;

  return selectedTime >= timeSlot.start && selectedTime < timeSlot.end;
};

const HomePage = () => {
  const { citySlug, city } = useCityContext();
  const [deals, setDeals] = useState([]);
  const [filteredDeals, setFilteredDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('random');
  const [currentFilter] = useState('all');
  const [userLocation, setUserLocation] = useState(null);
  const [isOpenNow, setIsOpenNow] = useState(false);
  const [selectedTime, setSelectedTime] = useState({ day: 'all', hour: 'all' });
  const [locationStatus, setLocationStatus] = useState('pending');
  const [showMap, setShowMap] = useState(false);

  // Generate SEO data for homepage (memoized for performance)
  const seoData = useMemo(() => {
    const cityName = city?.name || 'Tel Aviv';
    const siteUrl = city?.siteUrl || 'https://www.happyhourstlv.com';
    const venueCount = deals.length;

    const title = `Happy Hours in ${cityName} - Best Bar & Restaurant Deals | ${city?.displayName || 'Happy Hours TLV'}`;
    const description = venueCount > 0
      ? `Discover ${venueCount}+ happy hour deals at bars and restaurants in ${cityName}. Find the best drink specials, food deals, and promotions near you. Updated daily.`
      : `Discover the best happy hour deals at bars and restaurants in ${cityName}. Find drink specials, food deals, and promotions near you.`;

    return {
      title,
      description,
      canonicalUrl: siteUrl,
      ogImage: `${siteUrl}/images/site_preview_image_en.jpg`,
      keywords: city?.seo?.keywords,
      structuredData: generateHomepageStructuredData(deals, city)
    };
  }, [deals, city]);

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude
          });
          setLocationStatus('granted');
        },
        (error) => {
          console.error("Error getting location:", error);
          setLocationStatus('denied');
        }
      );
    } else {
      setLocationStatus('denied');
    }
  }, []);

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        setLoading(true);
        const happyHoursQuery = query(
          collection(db, 'venues'),
          where('city', '==', citySlug),
          where('category', '==', 'hh'),
          where('is_active', '==', true)
        );

        const querySnapshot = await getDocs(happyHoursQuery);
        logger.log('Query snapshot:', querySnapshot);

        if (querySnapshot.empty) {
          logger.log('No matching documents found');
          setDeals([]);
          setFilteredDeals([]);
          return;
        }

        const dealsData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          logger.log('Document data:', doc.id, data);

          let distance = null;
          if (userLocation && data.lat && data.lon) {
            distance = calculateDistance(
              userLocation.lat,
              userLocation.lon,
              data.lat,
              data.lon
            );
          }

          return {
            id: doc.id,
            headline: data.headline,
            address: trimAddress(data.address),
            main_offer: data.main_offer,
            offer_details: data.offer_details,
            days: data.days,
            phone: data.phone,
            link: data.link,
            image_url: data.image_url,
            place_id: data.place_id,
            lat: data.lat,
            lon: data.lon,
            category: data.category,
            opening_hours: data.opening_hours || {},
            randomValue: Math.random(),
            distance
          };
        });

        logger.log('Processed deals data:', dealsData);
        setDeals(dealsData);
        setFilteredDeals(dealsData);
      } catch (error) {
        console.error('Error fetching deals:', error);
        setDeals([]);
        setFilteredDeals([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDeals();
  }, [userLocation, citySlug]);

  useEffect(() => {
    let result = [...deals];

    // Apply search filter if exists
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(deal =>
        deal.headline?.toLowerCase().includes(searchLower) ||
        deal.main_offer?.toLowerCase().includes(searchLower) ||
        deal.address?.toLowerCase().includes(searchLower)
      );
    }

    // Apply time and day filters
    if (selectedTime.day !== 'all' || selectedTime.hour !== 'all') {
      result = result.filter(deal => {
        // If filtering by day
        if (selectedTime.day !== 'all') {
          const daySlots = deal.opening_hours?.[selectedTime.day] || [];

          // If also filtering by hour
          if (selectedTime.hour !== 'all') {
            return daySlots.some(slot => isTimeWithinSlot(selectedTime.hour, slot));
          }

          // If only filtering by day
          return daySlots.length > 0;
        }

        // If only filtering by hour
        if (selectedTime.hour !== 'all') {
          return Object.values(deal.opening_hours || {}).some(daySlots =>
            daySlots.some(slot => isTimeWithinSlot(selectedTime.hour, slot))
          );
        }

        return true;
      });
    }

    // Apply open now filter
    if (isOpenNow) {
      const now = new Date();
      const currentDay = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][now.getDay()];
      const currentTime = now.getHours() * 100 + now.getMinutes();

      result = result.filter(deal => {
        const todaySlots = deal.opening_hours?.[currentDay] || [];
        return todaySlots.some(slot =>
          slot &&
          typeof slot.start === 'number' &&
          typeof slot.end === 'number' &&
          currentTime >= slot.start &&
          currentTime < slot.end
        );
      });
    }

    // Apply sorting
    if (sortBy === 'distance' && userLocation) {
      result.sort((a, b) => {
        if (!a.distance || !b.distance) return 0;
        return parseFloat(a.distance) - parseFloat(b.distance);
      });
    } else if (sortBy === 'headline') {
      result.sort((a, b) => {
        const nameA = a.headline?.toLowerCase() || '';
        const nameB = b.headline?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });
    } else {
      result.sort((a, b) => a.randomValue - b.randomValue);
    }

    setFilteredDeals(result);
  }, [deals, searchTerm, sortBy, selectedTime, isOpenNow, userLocation]);

  const handleToggleMapView = () => {
    setShowMap(!showMap);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilterChange = ({ day, hour }) => {
    setSelectedTime({ day, hour });
  };

  const handleSortChange = (sortOption) => {
    if (sortOption === 'distance' && locationStatus !== 'granted') {
      return;
    }
    setSortBy(sortOption);
  };

  const handleOpenNowChange = (isOpen) => {
    setIsOpenNow(isOpen);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
      <SEOHead {...seoData} />
      <Header onToggleMap={handleToggleMapView} />

      <FilterBar
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        onSortChange={handleSortChange}
        onOpenNowChange={handleOpenNowChange}
        currentFilter={selectedTime.day}
        currentSort={sortBy}
        isOpenNow={isOpenNow}
        userLocation={userLocation}
        locationStatus={locationStatus}
      />

      <Container
        maxWidth="lg"
        sx={{
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          flexGrow: 1
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
            <CircularProgress sx={{ color: 'primary.main' }} />
          </Box>
        ) : showMap ? (
          <MapView
            deals={filteredDeals}
            onClose={handleToggleMapView}
          />
        ) : filteredDeals.length > 0 ? (
          <>
            {/* Mobile: Stack layout for horizontal cards */}
            <Box
              sx={{
                display: { xs: 'flex', sm: 'none' },
                flexDirection: 'column',
                gap: 2
              }}
            >
              {filteredDeals.map((venue) => (
                <DealCard key={venue.id} venue={venue} />
              ))}
            </Box>

            {/* Desktop: Grid layout for vertical cards */}
            <Grid
              container
              spacing={2.5}
              sx={{
                display: { xs: 'none', sm: 'flex' },
                justifyContent: 'center'
              }}
            >
              {filteredDeals.map((venue) => (
                <Grid
                  item
                  key={venue.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <DealCard venue={venue} />
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              px: 3
            }}
          >
            <SearchOffIcon
              sx={{
                fontSize: 64,
                color: 'text.secondary',
                opacity: 0.5,
                mb: 2
              }}
            />
            <Typography
              variant="h5"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                mb: 1
              }}
            >
              No happy hours found
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                opacity: 0.7
              }}
            >
              Try adjusting your filters or search term
            </Typography>
          </Box>
        )}
      </Container>

      <Footer />
    </Box>
  );
};

export default HomePage;
